import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import WorkoutTracker from './WorkoutTracker'
import { getActivityIconComponent, getWorkoutLabel, workoutActivities } from '../../common/workoutActivities'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Grid } from '@material-ui/core'
import { c, t } from 'ttag'
import { Card } from '../../components/Card/Card'
import { ActivityDto, ActivityRowType, GetUserActivities$Request, UserCompanyDto } from '../../services/api.types'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'
import Loader from '../../components/Loader/Loader'
import YumEmptyState from '../../components/YumEmptyState/YumEmptyState'
import { ReactComponent as EmptyStateIcon } from '../../assets/v3/icons/activities-empty-state.svg'
import YumHeader from '../../components/YumHeader/YumHeader'
import ActivityItem from './components/ActivityItem/ActivityItem'
import YumSearchField from '../../components/YumSearchField/YumSearchField'
import { WorkoutType } from '../../plugins/ActivityTrackerPlugin'
import { CustomTypeCategory, customTypes } from '../../services/CustomTypes'
import { DiscardConfirmationModal } from './components/DiscardConfirmationModal/DiscardConfirmationModal'
import { ReviewDuplicatesModal } from './components/ReviewDuplicatesModal/ReviewDuplicatesModal'
import ProgressChips, { ProgressChipOption } from '../Progress/components/ProgressChips'
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh'

// Sentinel chip value meaning "no type filter" (show all activity types).
const ALL_TYPES = 'ALL' as const
// Sentinel chip value for strength training. There's no coarse ActivityRowType for it (such rows
// fall under OTHER), so it filters purely by the normalized types below.
const STRENGTH = 'STRENGTH' as const
type ActivityTypeFilter = ActivityRowType | typeof ALL_TYPES | typeof STRENGTH

// The normalized workout types that make up the "Strength Training" chip. Sent as
// searchNormalizedType, where the back-end inverse-expands each to the raw provider strings.
const STRENGTH_NORMALIZED_TYPES: WorkoutType[] = ['strength_training', 'strength_training.functional', 'weightlifting']

export interface IActivitiesProps {
  userActivities: ActivityDto[]
  isLoading: boolean
  hasLoaded: boolean
  isLoadingMore: boolean
  hasMore: boolean
  offset: number
  userDto: any
  userCompanies: UserCompanyDto[]
  // Review duplicates modal
  reviewModalOpen: boolean
  duplicates: ActivityDto[]
  duplicatesMainId: string | null
  duplicatesLoading: boolean
  settingMain: boolean
}

export interface IActivitiesDispatchProps {
  getUserActivities: (request: GetUserActivities$Request) => void
  loadMoreActivities: (request: GetUserActivities$Request) => void
  deleteActivity: (request: { activityId: string }) => void
  openReviewDuplicates: (request: { activityId: string }) => void
  closeReviewDuplicates: () => void
  setMainActivity: (request: { activityId: string }) => void
}

// Function to get translated activity type label for search
const getActivityTypeLabel = (activityType: ActivityRowType): string => {
  switch (activityType) {
    case ActivityRowType.WALKING:
      return t`Walking`
    case ActivityRowType.RUNNING:
      return t`Running`
    case ActivityRowType.CYCLING:
      return t`Cycling`
    case ActivityRowType.SWIMMING:
      return t`Swimming`
    case ActivityRowType.YOGA:
      return t`Yoga`
    case ActivityRowType.MINDFULNESS:
      return t`Mindfulness`
    case ActivityRowType.OTHER:
      return t`Other`
    default:
      return c('Workout').t`Activity`
  }
}

// Function to find matching activity types based on search term
const findMatchingActivityTypes = (
  searchTerm: string,
): {
  matchingActivityTypes: ActivityRowType[]
  matchingNormalizedTypes: WorkoutType[]
} => {
  const searchLower = searchTerm.toLowerCase().trim()

  // Find matching ActivityRowType values
  const matchingActivityTypes: ActivityRowType[] = []
  Object.values(ActivityRowType).forEach((activityType) => {
    const activityLabel = getActivityTypeLabel(activityType)
    if (activityLabel.toLowerCase().includes(searchLower)) {
      matchingActivityTypes.push(activityType)
    }
  })

  // Find matching WorkoutType values — sent to the back-end as searchNormalizedType, where they
  // get inverse-expanded to the set of raw provider strings that map to each value.
  const matchingNormalizedTypes: WorkoutType[] = []
  workoutActivities.forEach(({ value, label }) => {
    if (label.toLowerCase().includes(searchLower)) {
      matchingNormalizedTypes.push(value as WorkoutType)
    }
  })

  return { matchingActivityTypes, matchingNormalizedTypes }
}

// Build the backend filter for a workout-type chip. Mirrors exactly what typing the type's label
// into the search box does — sending BOTH the coarse activity-row-type filter and the expanded
// normalized-type filter (which the backend matches against each activity's raw provider
// original_type). Sending only the row type misses activities whose row type is coarse/OTHER but
// whose original_type identifies them (e.g. Garmin "Open Water Swimming").
const buildTypeFilter = (
  type: ActivityTypeFilter,
): Pick<GetUserActivities$Request, 'searchActivityType' | 'searchNormalizedType'> => {
  if (type === ALL_TYPES) return {}
  if (type === STRENGTH) return { searchNormalizedType: STRENGTH_NORMALIZED_TYPES }
  const { matchingActivityTypes, matchingNormalizedTypes } = findMatchingActivityTypes(getActivityTypeLabel(type))
  return { searchActivityType: matchingActivityTypes, searchNormalizedType: matchingNormalizedTypes }
}

export default function Activities(props: IActivitiesProps & IActivitiesDispatchProps) {
  const {
    userActivities,
    isLoading,
    hasLoaded,
    isLoadingMore,
    hasMore,
    offset,
    userDto,
    userCompanies,
    reviewModalOpen,
    duplicates,
    duplicatesMainId,
    duplicatesLoading,
    settingMain,
    getUserActivities,
    loadMoreActivities,
    deleteActivity,
    openReviewDuplicates,
    closeReviewDuplicates,
    setMainActivity,
  } = props

  // Search state management
  const [searchValue, setSearchValue] = useState<string>('')
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [hasNoMatches, setHasNoMatches] = useState<boolean>(false)
  // Currently selected workout-type chip filter ('ALL' means no type filter).
  const [selectedType, setSelectedType] = useState<ActivityTypeFilter>(ALL_TYPES)

  // Show the loader instead of the "no activities" empty state until the first fetch completes
  // (`!hasLoaded` also covers the first paint, before the mount effect has even dispatched the
  // fetch) and during any refetch that has nothing to show. A refetch with results on screen
  // (chip/search change) keeps the current list visible instead of flashing the loader.
  const loading = (isLoading || !hasLoaded) && !userActivities.length
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // No more client-side filtering - activities are filtered by the backend
  // The userActivities prop now contains the filtered results from the server

  // Debounced search function that actually calls the API
  const performSearch = useCallback(
    (value: string) => {
      setIsSearching(true)
      setHasNoMatches(false) // Reset no matches state

      if (value.trim()) {
        // Find matching activity types and send them to backend
        const { matchingActivityTypes, matchingNormalizedTypes } = findMatchingActivityTypes(value)

        // If search term provided but no matches found, don't make API request
        // The component will show zero activities (empty state)
        if (matchingActivityTypes.length === 0 && matchingNormalizedTypes.length === 0) {
          setHasNoMatches(true)
          setIsSearching(false)
          return
        }

        // Create search request with matching types
        const searchRequest: GetUserActivities$Request = {
          size: 20,
          start: 0,
          searchActivityType: matchingActivityTypes,
          searchNormalizedType: matchingNormalizedTypes,
        }

        // Call API with search parameters
        getUserActivities(searchRequest)
      } else {
        // Empty search - show all activities
        getUserActivities({ size: 20, start: 0 })
      }

      setIsSearching(false)
    },
    [getUserActivities],
  )

  // Pull-to-refresh re-runs the currently active query from the first page.
  const handleRefresh = useCallback(() => {
    if (searchValue.trim()) {
      performSearch(searchValue)
    } else {
      getUserActivities({ size: 20, start: 0, ...buildTypeFilter(selectedType) })
    }
  }, [searchValue, selectedType, performSearch, getUserActivities])

  // Handle search value changes with debounced API call
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      // Free-text search and the chip filter are mutually exclusive type filters, so typing
      // resets the chip back to "All".
      setSelectedType(ALL_TYPES)

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Set new timeout for debounced search
      debounceTimeoutRef.current = setTimeout(() => {
        performSearch(value)
      }, 300) // 300ms debounce delay
    },
    [performSearch],
  )

  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    setSelectedType(ALL_TYPES)
    setIsSearching(false)
    setHasNoMatches(false)

    // Clear any pending debounced search
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
      debounceTimeoutRef.current = null
    }

    // Reset to show all activities
    getUserActivities({ size: 20, start: 0 })
  }, [getUserActivities])

  // Handle workout-type chip selection. Selecting a chip clears any free-text search (the two
  // are mutually exclusive type filters) and re-queries the backend filtered by that row type.
  const handleTypeChange = useCallback(
    (type: ActivityTypeFilter) => {
      setSelectedType(type)
      setSearchValue('')
      setHasNoMatches(false)

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }

      const request: GetUserActivities$Request = { size: 20, start: 0, ...buildTypeFilter(type) }
      getUserActivities(request)
    },
    [getUserActivities],
  )

  // Function to load more activities
  const handleLoadMore = useCallback(() => {
    // Skip while a base fetch (initial load / filter / search) is in flight: it resets the offset
    // to 0, so a load-more firing now would re-request and append page 0, duplicating rows.
    if (!isLoadingMore && !isLoading && hasMore) {
      // Create load more request with current filter parameters
      const loadMoreRequest: GetUserActivities$Request = { size: 20, start: offset }

      // Carry the active filter: a selected chip takes precedence, otherwise the free-text search.
      if (selectedType !== ALL_TYPES) {
        Object.assign(loadMoreRequest, buildTypeFilter(selectedType))
      } else if (searchValue.trim()) {
        const { matchingActivityTypes, matchingNormalizedTypes } = findMatchingActivityTypes(searchValue)
        if (matchingActivityTypes.length > 0 || matchingNormalizedTypes.length > 0) {
          loadMoreRequest.searchActivityType = matchingActivityTypes
          loadMoreRequest.searchNormalizedType = matchingNormalizedTypes
        }
      }

      loadMoreActivities(loadMoreRequest)
    }
  }, [isLoadingMore, isLoading, hasMore, offset, loadMoreActivities, searchValue, selectedType])

  // Infinite scroll via a scroll listener.
  // IntersectionObserver is unreliable in this layout: an ancestor (`.layout`) has
  // `overflow: hidden`, which prevents IO's viewport-rooted callbacks from ever firing. So we
  // listen for scroll (in the capture phase, which also catches scrolls inside nested scroll
  // containers such as the mobile `.layout__content` scroller) and load the next page when the
  // sentinel nears the bottom of the viewport. Re-runs on list changes so a freshly appended page
  // immediately triggers another load if the sentinel is still in view.
  useEffect(() => {
    if (!hasMore) return

    const checkAndLoad = () => {
      const sentinel = loadMoreRef.current
      if (!sentinel) return
      // getBoundingClientRect().top is viewport-relative regardless of which element scrolled.
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + 300) {
        handleLoadMore()
      }
    }

    window.addEventListener('scroll', checkAndLoad, true)
    window.addEventListener('resize', checkAndLoad)
    // Check immediately in case the sentinel is already visible (e.g. a short first page).
    checkAndLoad()

    return () => {
      window.removeEventListener('scroll', checkAndLoad, true)
      window.removeEventListener('resize', checkAndLoad)
    }
  }, [hasMore, isLoadingMore, handleLoadMore, userActivities.length])

  useEffect(() => {
    // Fetch activities on component mount with initial pagination
    getUserActivities({ size: 20, start: 0 })
  }, [getUserActivities])

  // Cleanup debounce timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  // Leading chip icon for a workout type, reusing the activity-card icon of the type's
  // representative normalized value.
  const typeFilterIcon = (normalizedType: string): React.ReactElement | undefined => {
    const IconComponent = getActivityIconComponent(normalizedType)
    return IconComponent ? <IconComponent /> : undefined
  }

  // Workout-type quick-filter chips. Values map to the coarse ActivityRowType grouping the
  // backend filters by, so e.g. "Swimming" also covers Open Water / Pool swimming.
  const typeFilterOptions: ProgressChipOption[] = [
    { label: t`All`, value: ALL_TYPES },
    {
      label: getActivityTypeLabel(ActivityRowType.WALKING),
      value: ActivityRowType.WALKING,
      icon: typeFilterIcon('walking'),
    },
    {
      label: getActivityTypeLabel(ActivityRowType.RUNNING),
      value: ActivityRowType.RUNNING,
      icon: typeFilterIcon('running'),
    },
    {
      label: getActivityTypeLabel(ActivityRowType.CYCLING),
      value: ActivityRowType.CYCLING,
      icon: typeFilterIcon('cycling'),
    },
    {
      label: getActivityTypeLabel(ActivityRowType.SWIMMING),
      value: ActivityRowType.SWIMMING,
      icon: typeFilterIcon('swimming'),
    },
    { label: t`Strength training`, value: STRENGTH, icon: typeFilterIcon('strength_training') },
    { label: getActivityTypeLabel(ActivityRowType.YOGA), value: ActivityRowType.YOGA, icon: typeFilterIcon('yoga') },
    {
      label: getActivityTypeLabel(ActivityRowType.MINDFULNESS),
      value: ActivityRowType.MINDFULNESS,
      icon: typeFilterIcon('meditation'),
    },
    { label: getActivityTypeLabel(ActivityRowType.OTHER), value: ActivityRowType.OTHER, icon: typeFilterIcon('other') },
  ]

  return (
    <Card>
      <PullToRefresh onRefresh={handleRefresh} refreshing={isLoading}>
        <NotificationsWrapper />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: '1rem' }}
          wrap="nowrap"
        >
          <Grid item style={{ overflow: 'hidden' }}>
            <h1
              style={{ marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >{t`Activities`}</h1>
          </Grid>
          {hasFeatureFlag(userCompanies, 'recordActivity') && (
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              <WorkoutTracker onActivityUploaded={() => getUserActivities({ size: 20, start: 0 })} userDto={userDto} />
            </Grid>
          )}
        </Grid>
        {/* Search field for filtering activities by type */}
        <div style={{ marginBottom: '0.5rem' }}>
          <YumSearchField
            value={searchValue}
            onChange={handleSearchChange}
            /*placeholder={t`Search activities by type...`}*/
            autoFocus={false}
            onClear={handleSearchClear}
          />
        </div>
        {/* Quick workout-type filters */}
        <div style={{ marginBottom: '1rem' }}>
          <ProgressChips
            options={typeFilterOptions}
            value={selectedType}
            onChange={handleTypeChange}
            ariaLabel={t`Filter activities by type`}
            style={{ marginTop: 0 }}
            scroll
          />
        </div>
        {loading || isSearching ? (
          <Loader />
        ) : !userActivities.length || hasNoMatches ? (
          <YumEmptyState text={t`No activities found...`} Icon={EmptyStateIcon} extraPx={30} />
        ) : (
          <div>
            {/* Show activities returned from server (already filtered) */}
            {userActivities.map((activity) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                onDeleteActivity={(activityId) => deleteActivity({ activityId })}
                onReviewDuplicates={() => openReviewDuplicates({ activityId: activity.id })}
              />
            ))}

            {/* Infinite-scroll sentinel: observed to trigger loading the next page. */}
            {hasMore && (
              <div
                ref={loadMoreRef}
                style={{ display: 'flex', justifyContent: 'center', minHeight: '40px', marginTop: '16px' }}
              >
                {isLoadingMore && <Loader top noPadding />}
              </div>
            )}
          </div>
        )}

        <ReviewDuplicatesModal
          open={reviewModalOpen}
          onClose={closeReviewDuplicates}
          activities={duplicates}
          mainActivityId={duplicatesMainId}
          loading={duplicatesLoading}
          settingMain={settingMain}
          onConfirmMain={(activityId) => setMainActivity({ activityId })}
          onDeleteActivity={(activityId) => deleteActivity({ activityId })}
        />
      </PullToRefresh>
    </Card>
  )
}
